import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";

/**
 * Fetches an artifact image and returns a blob URL.
 * Uses axios with auth headers for authenticated requests.
 */
const fetchArtifactImage = async (artifactId: number): Promise<string> => {
  const response = await axios.get(
    `${nuclearn_api_url}/artifacts/${artifactId}/content`,
    { responseType: "blob" },
  );
  return URL.createObjectURL(response.data);
};

/**
 * React Query hook for fetching an artifact image.
 * Returns a blob URL that can be used as an img src.
 *
 * Blob URL lifecycle:
 * - On refetch (URL changes): previous blob URL is revoked immediately.
 * - On unmount when no other observers remain: blob URL is revoked,
 *   preventing slow memory leaks in long-lived sessions.
 *
 * @param artifactId - The artifact ID to fetch, or null/undefined to disable
 * @returns Query result with blob URL data
 */
export const useImageArtifact = (artifactId: number | null | undefined) => {
  const queryClient = useQueryClient();
  const previousUrlRef = useRef<string | null>(null);

  const queryKey = ["image-artifact", artifactId];

  const query = useQuery(queryKey, () => fetchArtifactImage(artifactId!), {
    enabled: typeof artifactId === "number" && artifactId > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Revoke previous blob URL when the URL changes (on refetch).
  useEffect(() => {
    const prevUrl = previousUrlRef.current;
    if (prevUrl && prevUrl !== query.data) {
      URL.revokeObjectURL(prevUrl);
    }
    previousUrlRef.current = query.data ?? null;
  }, [query.data]);

  // Revoke blob URL on unmount when this is the last observer.
  // Deferred via setTimeout so that React Query's own effect cleanup
  // (which removes the observer) has completed before we check the count.
  useEffect(() => {
    const id = artifactId;
    return () => {
      if (typeof id !== "number" || id <= 0) return;

      setTimeout(() => {
        const cachedQuery = queryClient
          .getQueryCache()
          .find(["image-artifact", id]);
        if (cachedQuery && cachedQuery.getObserversCount() === 0) {
          const blobUrl = cachedQuery.state.data as string | undefined;
          if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
          }
          queryClient.removeQueries(["image-artifact", id]);
        }
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId, queryClient]);

  return query;
};
